import React, { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import Button, { ButtonTypes } from "../../../../components/Button";
import {
  Field,
  FieldLabel,
  ToneSegments,
} from "../../../../components/FormPanel";
import { noListingMessage } from "../../../../components/Input/InstrumentSearch";
import { CatalogueSearchResult } from "../../../../services/instruments";
import { PairAssetType } from "../../../../services/tickerRequests";
import {
  countryOptionOf,
  METHOD_OPTIONS,
  PAIR_EXCHANGES,
  soleListingOptionOf,
} from "./constants";
import { CountryField } from "./CountryField";
import { ExistingInstrumentField } from "./ExistingInstrumentField";
import { ListingFields } from "./ListingFields";
import { ManualFields } from "./ManualFields";
import { PairFields } from "./PairFields";
import { EditorActions, EditorError, EditorFields, EditorForm } from "./styles";
import { defaultValuesOf, identityOf, toSelection } from "./toSelection";
import {
  InstrumentEditorProps,
  InstrumentEditorValues,
  InstrumentMethod,
  MethodOption,
} from "./types";
import { useListingDiscovery } from "./useListingDiscovery";

export const InstrumentEditor: React.FC<InstrumentEditorProps> = ({
  initialIdentity,
  submitLabel,
  isSubmitting,
  error,
  draft,
  onDraftChange,
  onSubmit,
}) => {
  const [method, setMethod] = useState<InstrumentMethod>(
    draft?.method ?? "EXISTING",
  );
  const [pristineValues] = useState(() => defaultValuesOf(initialIdentity));
  const { control, handleSubmit, setValue, getValues, trigger, clearErrors } =
    useForm<InstrumentEditorValues>({
      defaultValues: draft?.values ?? pristineValues,
    });

  const [symbol, exchange, listing, pairAssetType] = useWatch({
    control,
    name: ["symbol", "exchange", "listing", "pairAssetType"],
  });
  const identity = identityOf({ symbol, exchange });
  const discovery = useListingDiscovery(identity, draft?.discovery);
  const watchedValues = useWatch({ control });
  // A restored LISTED draft needs its candidates to approve; a failed search has nothing worth keeping.
  const keptDiscovery =
    discovery.result && "candidates" in discovery.result
      ? discovery.result
      : null;

  useEffect(() => {
    const values = getValues();
    const isPristine =
      method === "EXISTING" &&
      keptDiscovery === null &&
      JSON.stringify(values) === JSON.stringify(pristineValues);
    onDraftChange?.(
      isPristine ? null : { method, values, discovery: keptDiscovery },
    );
  }, [
    watchedValues,
    method,
    keptDiscovery,
    pristineValues,
    getValues,
    onDraftChange,
  ]);

  const handleFind = async () => {
    if (!(await trigger(["symbol", "exchange"]))) {
      return;
    }
    setValue("listing", soleListingOptionOf(await discovery.find(identity)));
  };

  const handleFindClick = () => void handleFind();

  const handlePickExisting = (instrument: CatalogueSearchResult | null) =>
    setValue("country", countryOptionOf(instrument?.sourceCountry ?? null));

  const handleMethodChange = ({ value }: MethodOption) => {
    if (value === method) {
      return;
    }
    clearErrors();
    setMethod(value);
    // A catalog row's country must not follow the admin onto a different instrument.
    handlePickExisting(
      value === "EXISTING"
        ? (getValues("instrument")?.instrument ?? null)
        : null,
    );
  };

  const submit = (values: InstrumentEditorValues) => {
    const selection = toSelection(method, values);
    if (selection) {
      onSubmit(selection);
    }
  };

  const isListingChosen = discovery.candidates.some(
    ({ yahooSymbol }) => yahooSymbol === listing?.value,
  );
  const isSubmitBlocked = method === "LISTED" && !isListingChosen;
  const noMatchMessage = discovery.hasNoMatch
    ? `${noListingMessage(identity.symbol, identity.exchange)} You can also switch to another method.`
    : null;
  const pairExchange = PAIR_EXCHANGES[pairAssetType.value as PairAssetType];

  return (
    <EditorForm noValidate onSubmit={handleSubmit(submit)}>
      <Field>
        <FieldLabel as="span">Add the ticker from</FieldLabel>
        <ToneSegments
          groupLabel="Instrument source"
          options={METHOD_OPTIONS}
          selectedValue={method}
          disabled={isSubmitting}
          onChange={handleMethodChange}
        />
      </Field>
      <EditorFields>
        {method === "EXISTING" && (
          <ExistingInstrumentField
            control={control}
            disabled={isSubmitting}
            onPick={handlePickExisting}
          />
        )}
        {method === "LISTED" && (
          <ListingFields
            control={control}
            disabled={isSubmitting}
            candidates={discovery.candidates}
            noMatchMessage={noMatchMessage}
            discoveryError={discovery.error}
            isFinding={discovery.isFinding}
            onFind={handleFindClick}
          />
        )}
        {method === "PAIR" && (
          <PairFields
            control={control}
            disabled={isSubmitting}
            exchange={pairExchange}
          />
        )}
        {method === "MANUAL" && (
          <ManualFields control={control} disabled={isSubmitting} />
        )}
        {method !== "PAIR" && (
          <CountryField
            control={control}
            label="Source country (optional)"
            disabled={isSubmitting}
          />
        )}
      </EditorFields>
      {error && <EditorError role="alert">{error}</EditorError>}
      <EditorActions>
        <Button
          type={ButtonTypes.primary}
          buttonType="submit"
          disabled={isSubmitBlocked}
          isLoading={isSubmitting}
        >
          {submitLabel}
        </Button>
      </EditorActions>
    </EditorForm>
  );
};
